import { useEffect, useState } from 'react';
import { log } from '../app/logger';
import { loadSettings } from '../app/settingsB';

/**
 * B spec §2.3 / §2.4 — **the theme, read once at launch and applied to the document**
 * (build unit B-19).
 *
 * `styles.css` has carried the three-way selector set since B-0; nothing has ever set the
 * attribute it keys on. This module is that missing half, and it is deliberately tiny: one read,
 * one attribute, one vocabulary.
 *
 * ── Three states, not two, and the CSS already says so ───────────────────────
 *
 * | Stored `themePreference` | `data-theme` | What the user sees |
 * |---|---|---|
 * | `'light'` | `light` | light, on a dark-set device too (the explicit choice wins) |
 * | `'dark'` | `dark` | dark, on a light-set device too |
 * | absent / unrecognised | *(no attribute)* | the device's own setting, via `prefers-color-scheme` |
 *
 * The third row is not an oversight in the control above it (§2.4 offers two choices, Light and
 * Dark). It is the state a device is in before anyone has chosen, and `styles.css` guards its
 * dark block with `:not([data-theme="light"])` precisely so that an explicit light choice
 * overrides a dark device. A More tab that showed "Light" as selected on a phone rendering dark
 * would be describing a preference that does not exist; it says which one is stored and, when
 * none is, that the device is deciding.
 *
 * ── The bounded first-paint flash, named rather than hidden ──────────────────
 *
 * The preference lives in IndexedDB, so it cannot be read synchronously before first paint. A
 * device whose system setting disagrees with the stored choice therefore paints the system's
 * answer for the length of one settings read (the same window `AppB` spends resolving where to
 * resume — the app is showing "Finding where you left off…" throughout). The alternatives were
 * both worse: blocking first paint on a database open, or mirroring the choice into
 * `localStorage` and owning two sources of truth for one fact. Recorded here so the next reader
 * knows it is a decision.
 */

const MODULE = 'ui-b/theme';

/** §2.4's two offered choices. `null` is the third state — no choice stored. */
export type ThemeChoiceB = 'light' | 'dark';

/** The vocabulary as values, for the control to render and for the parser below. */
export const THEME_CHOICES_B = ['light', 'dark'] as const satisfies readonly ThemeChoiceB[];

/**
 * A stored setting value read as a theme choice, or `null` for "no choice stored".
 *
 * `SettingRow.value` is `unknown` by the engine's own contract (`app/settingsB.ts`: the closed
 * *key* space is the engine's business, the value vocabulary is this surface's), and an archive
 * import writes settings rows this app has never seen. So anything that is not one of the two
 * words is the third state, not a crash and not a silent coercion to light — a device following
 * its own setting is a defensible thing to be, which is what makes this parse total.
 */
export function themeChoiceFromB(value: unknown): ThemeChoiceB | null {
  return value === 'light' || value === 'dark' ? value : null;
}

/**
 * Sets (or removes) `data-theme` on the document element.
 *
 * Removal is the point of the `null` arm: leaving a stale attribute behind would pin the device
 * to the last choice after an import wiped the row that justified it.
 */
export function applyThemeB(choice: ThemeChoiceB | null): void {
  const root = document.documentElement;
  if (choice === null) root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
}

/**
 * Reads the stored preference once at launch and applies it. Returns the number of completed
 * reads, which `AppB` renders as an attribute — the `data-resolved` device, so a browser pass
 * waits on an instant the app guarantees rather than on a sleep. (An unset preference sets no
 * attribute at all, so "the theme has been applied" is otherwise unobservable.)
 *
 * The **only** other writer of the attribute is the More tab, which calls {@link applyThemeB}
 * directly after its own write commits — so the change is instant rather than next-launch, and
 * the document attribute stays the single piece of shared state between the two (no context, no
 * prop drilling through the tab shell, and nothing to fall out of step).
 *
 * A read failure is logged and leaves the device on its own setting — a theme is the one piece of
 * state whose failure mode may safely be "the app still works, in the other colours".
 */
export function useThemeB(): { readonly reads: number } {
  const [reads, setReads] = useState(0);

  useEffect(() => {
    let live = true;
    void loadSettings()
      .then((settings) => {
        if (live) applyThemeB(themeChoiceFromB(settings.themePreference));
      })
      .catch((cause: unknown) => {
        log.error(MODULE, 'B §2.3: the theme preference could not be read', {
          message: cause instanceof Error ? cause.message : String(cause),
        });
      })
      .finally(() => {
        if (live) setReads((n) => n + 1);
      });
    return () => {
      live = false;
    };
  }, []);

  return { reads };
}
